import "./index.css";
import Layout from "./ui/Layout";
import { checkAccess } from "Auth";
import React, { useMemo, useRef, useState } from "react";
import { request } from "../../request";
import Status from "../components/Status";
import { useDebounceEffect, useRequest } from "ahooks";
import Publish from "./components/Publish";
import EditFAQs from "./components/EditFAQs";
import Collapse from "../components/Collapse";
import { PERSONAL_ITEMS } from "../ProfileConfig";
import WebsiteInfo from "../components/WebsiteInfo";
import { WarningOutlined } from "@ant-design/icons";
import SwitchExpand from "../components/SwitchExpand";
import { UR_LoanOfficerManager, UR_Owner } from "State";
import EditTestimonial from "./components/EditTestimonial";
import EditPageBranding from "./components/EditPageBranding";
import { Button, Form, Input, message, Spin, Tooltip } from "antd";
import { getCustomerId } from "Common";
import EditPersonalSiteUrl from "./components/EditPersonalSiteUrl";
import EditMortgageRateCalculator from "./components/EditMortgageRateCalculator";
import PersonalSiteCompanyInfo, {
  blueratePreviewEmptyDescription,
  fetchPersonalSiteCompanyOptions,
  formatBluerateOrgLabel,
  isBlueratePreviewReady,
  canSwitchToManageBluerate,
  canManageBluerateOption,
  chooseNewOrganizationReason,
  isReachableCompanyOption,
  optionOrganizationName,
  resolveBluerateCustomerID,
  type PersonalSiteCompanyInfoHandle,
} from "./components/PersonalSiteCompanyInfo";
import {
  switchOrganization,
} from "../../BranchSwitcher/switchOrganization";
import { track } from "statsig";
import { STATSIG } from "statsig/events";

import { IS_CUSTOMIZED_CUSTOMER, SHOW_WIDGETS_FEATURES } from "..";
import ICON_USER from "../assets/user.svg";
import ICON_MESSAGE from "../assets/msg.svg";
import ICON_WEBSITE from "../assets/website.svg";
import ICON_BLUERATE from "../assets/bluerate.png";
import ICON_CALCULATOR from "../assets/calculator.svg";
import { ENUM_PERSONAL_SITE } from "BlueratePersonalSite/enum";

import type { TypeResponseData } from "..";

export interface TypeEditPreviewProps {
  /**
   * @param data 数据状态
   */
  data: TypeResponseData;
  /**
   * @param sync 是否正在上传数据
   */
  sync: boolean;
  /**
   * @param loading 是否正在加载数据
   */
  loading: boolean;
  /**
   * @param status 当前状态
   */
  status: ENUM_PERSONAL_SITE.STATUS;
  /**
   * @name onUpdateData 更新数据
   */
  onUpdateData(): Promise<void>;
  /**
   * @param getStatus 获取审核状态
   */
  getStatus(): Promise<void>;
}

// 是否可编辑 “Mortgage Rate Calculator”
export const SHOW_MORTGAGE = checkAccess([UR_Owner, UR_LoanOfficerManager]);

function PageContentSwitch({
  event,
  onChange,
  children,
  ...props
}: React.ComponentProps<typeof SwitchExpand> & {
  event:
    | typeof STATSIG.PERSONAL_SITE.MORTGAGE_CALCULATOR_SWITCH
    | typeof STATSIG.PERSONAL_SITE.AFFORDABILITY_CALCULATOR_SWITCH
    | typeof STATSIG.PERSONAL_SITE.FAQ_SWITCH
    | typeof STATSIG.PERSONAL_SITE.TESTIMONIAL_SWITCH
    | typeof STATSIG.PERSONAL_SITE.HOME_EQUITY_SWITCH
}) {
  return (
    <SwitchExpand
      {...props}
      onChange={(checked, e) => {
        const type = checked ? "open" : "hide";
        track(event, type, { type });
        onChange?.(checked, e);
      }}
    >
      {children}
    </SwitchExpand>
  )
}

// 重新审核提示语句（部分字段重新编辑会导致重新进入审核流程）
const AUDIT_TIPS = {
  title: `Editing this field will re-enter the review process, so please edit with caution!`,
  icon: <WarningOutlined rev={undefined} />,
};

const { origin } = window.location;
const BLUERATE_ENV = { us: "staging", net: "dev" };
const CUSTOMER_ID = getCustomerId() || "";

/**
 * Where the company logo the bluerate page shows loads from: a hosted logo as
 * is, one kept as image data through /data/companylogo on this host; "" when
 * the company has none.
 */
function companyLogoURL(logo: unknown): string {
  const value = typeof logo === "string" ? logo.trim() : "";
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `/data/companylogo/${encodeURIComponent(CUSTOMER_ID)}?v=${value.length}`;
}

/**
 * @name EditPreview 完整设置和发布
 * @description 初始化后，发布
 */
const EditPreview: React.FC<TypeEditPreviewProps> = ({
  data,
  sync,
  status,
  loading,
  getStatus,
  onUpdateData,
}) => {
  const form = Form.useFormInstance();
  const [url, setUrl] = useState({ personal: "", bluerate: "" });
  const bluerateCompanyName = Form.useWatch(
    ["LandingPageSetUp", "BluerateCompanyName"],
    form,
  );
  const bluerateSourceCustomerID = Form.useWatch(
    ["LandingPageSetUp", "BluerateCompanySourceCustomerID"],
    form,
  );
  const {
    data: companyOptions = [],
    loading: companyOptionsLoading,
    refreshAsync: refreshCompanyOptions,
  } = useRequest(fetchPersonalSiteCompanyOptions);
  const selectedBluerateCustomerID = resolveBluerateCustomerID(
    bluerateSourceCustomerID,
    companyOptions,
  );
  const selectedBluerateOption = useMemo(
    () =>
      companyOptions.find(
        (item) => item.customerId === selectedBluerateCustomerID,
      ),
    [companyOptions, selectedBluerateCustomerID],
  );
  const selectedBluerateOrgName = optionOrganizationName(selectedBluerateOption) || String(
    bluerateCompanyName || "",
  ).trim();
  const selectedBluerateLabel = formatBluerateOrgLabel(
    selectedBluerateOrgName,
    selectedBluerateOption,
  );
  const sourceCustomerID = String(bluerateSourceCustomerID || "").trim();
  const managedOrgReachable = canSwitchToManageBluerate(
    selectedBluerateCustomerID,
    companyOptions,
  );
  const profileCustomerID = sourceCustomerID || selectedBluerateCustomerID;
  const showChooseNewOrganization =
    !companyOptionsLoading &&
    Boolean(selectedBluerateOption) &&
    !canManageBluerateOption(selectedBluerateOption);
  // Until company-options returns, do not fall through to "edit current org"
  // just because the primary id is not reachable in an empty list yet.
  const canEditBluerate =
    !showChooseNewOrganization &&
    (!selectedBluerateCustomerID
      ? !companyOptionsLoading
      : selectedBluerateCustomerID === CUSTOMER_ID ||
        (!companyOptionsLoading &&
          !isReachableCompanyOption(
            selectedBluerateCustomerID,
            companyOptions,
          )));
  const companyInfoRef = useRef<PersonalSiteCompanyInfoHandle>(null);

  const [load, setLoad] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);

  // 正在审核
  const IS_WAIT = status === ENUM_PERSONAL_SITE.STATUS.WAIT;

  // 未初始化
  const IS_UN_INIT = status === ENUM_PERSONAL_SITE.STATUS.UN_INIT;

  // 通过审核
  const IS_ACTIVATED = status === ENUM_PERSONAL_SITE.STATUS.ACTIVATED;

  // Bluerate 预览始终跟 primary org 走。组织列表未返回前先 loading，
  // 避免用当前会话组织的 UN_INIT 画出错误空态。
  const blueratePreviewLoading = companyOptionsLoading;
  const blueratePreviewReady = blueratePreviewLoading
    ? false
    : showChooseNewOrganization
      ? false
      : canEditBluerate
        ? !IS_UN_INIT
        : isBlueratePreviewReady(selectedBluerateOption);
  const blueratePreviewOrgLabel =
    selectedBluerateLabel ||
    selectedBluerateOrgName ||
    String(data?.company?.Name || "").trim() ||
    "this organization";
  const blueratePreviewEmpty =
    blueratePreviewLoading || blueratePreviewReady
      ? null
      : blueratePreviewEmptyDescription(blueratePreviewOrgLabel, {
          canEdit: canEditBluerate && !showChooseNewOrganization,
          cannotManage: showChooseNewOrganization,
        });

  async function goToBluerateOrganization() {
    const targetId = selectedBluerateCustomerID;
    if (!targetId || targetId === CUSTOMER_ID || switchingOrg) return;
    if (!managedOrgReachable) {
      companyInfoRef.current?.openPicker();
      return;
    }
    try {
      setSwitchingOrg(true);
      await switchOrganization(targetId, {
        redirectTo: "/app/landingpage",
        notice: `Switched to ${blueratePreviewOrgLabel} — opened its Bluerate settings`,
      });
    } catch (error) {
      setSwitchingOrg(false);
      const text = error instanceof Error ? error.message : "";
      if (/disabled/i.test(text)) {
        message.warning(
          "That organization is no longer available. Choose a new one to manage your Bluerate profile.",
        );
        companyInfoRef.current?.openPicker();
        return;
      }
      message.error(text || "Failed to switch organization");
    }
  }

  const blueratePreviewDescription = blueratePreviewEmpty ? (
    <span>
      {blueratePreviewEmpty.before}
      {blueratePreviewEmpty.linkText ? (
        <button
          type="button"
          className="bluerate-preview-switch-link"
          disabled={switchingOrg}
          onClick={goToBluerateOrganization}
        >
          {blueratePreviewEmpty.linkText}
        </button>
      ) : null}
      {blueratePreviewEmpty.after}
    </span>
  ) : undefined;

  // 初始化
  async function onCompleteSetupPublish() {
    if (sync || load) return;
    try {
      setLoad(true);
      await checkHasLenders();
      const body = await form.validateFields();
      const bodyLP = body.LandingPageSetUp; // 表单存储的
      const resLP = data.LandingPageSetUp; // 服务器返回的
      const bluerateCompany = {
        Name:
          bodyLP.BluerateCompanyName ||
          resLP.BluerateCompanyName ||
          bodyLP.CompanyName ||
          resLP.CompanyName,
        Address:
          bodyLP.BluerateCompanyAddress ||
          resLP.BluerateCompanyAddress ||
          bodyLP.CompanyAddress ||
          resLP.CompanyAddress ||
          data.company.Address,
        Zip:
          bodyLP.BluerateCompanyZip ||
          resLP.BluerateCompanyZip ||
          bodyLP?.CompanyZip ||
          resLP.CompanyZip ||
          undefined,
        City:
          bodyLP.BluerateCompanyCity ||
          resLP.BluerateCompanyCity ||
          bodyLP.CompanyCity ||
          resLP.CompanyCity ||
          undefined,
        State:
          bodyLP.BluerateCompanyState ||
          resLP.BluerateCompanyState ||
          bodyLP.CompanyState ||
          resLP.CompanyState ||
          undefined,
        County:
          bodyLP.BluerateCompanyCounty ||
          resLP.BluerateCompanyCounty ||
          bodyLP.CompanyCounty ||
          resLP.CompanyCounty ||
          undefined,
      };
      await request("/los/confirmmarketplacelo", {
        isSuccess: (res) => res.status === 200,
        method: "POST",
        body: {
          NMLS: body.NMLS,
          Phone: body.Phone,
          LastName: body.LastName,
          FirstName: body.FirstName,
          Email: bodyLP.Email,
          LoanTypes: bodyLP.LoanTypes,
          LicensedStates: bodyLP.States,
          // Marketplace/Bluerate company info is separate from personal site company info.
          Company: bluerateCompany,
        },
      });
      // onUpdateData already refreshes getverifystatus — do not call getStatus()
      // in parallel (ahooks cancels the older runAsync with a never-resolving Promise).
      await Promise.all([onUpdateData(), refreshCompanyOptions()]);
      track(STATSIG.PERSONAL_SITE.COMPLETE_SETUP);
    } catch (e) {
      console.log("@-onCompleteSetupPublish", e);
      if (e?.errorFields?.length) {
        message.warning(
          "The content entered is incorrect. Please check and try again.",
        );
      }
    } finally {
      setLoad(false);
    }
  }

  // Unpublish、Publish
  async function onPublishStatusChange() {
    const IS_ACTIVATED = status === ENUM_PERSONAL_SITE.STATUS.ACTIVATED;
    try {
      setLoad(true);
      !IS_ACTIVATED && (await checkHasLenders());
      await onUpdateData(); // 必须先更新在去 “Publish”
      await request("/los/setverifystatus", {
        method: "POST",
        body: {
          nmls: form.getFieldValue("NMLS"),
          status: IS_ACTIVATED
            ? ENUM_PERSONAL_SITE.STATUS.DEACTIVATED
            : ENUM_PERSONAL_SITE.STATUS.WAIT,
        },
      });
      await getStatus();
      const type = IS_ACTIVATED ? "unpublish" : "publish";
      track(STATSIG.PERSONAL_SITE.PUBLISH, type, { type });
    } catch (e) {
      console.log("@-onPublishStatusChange", e);
    } finally {
      setLoad(false);
    }
  }

  // 若 “Mortgage Rate Calculator” 可用
  // 那么 “Publish”、“Complete Setup” 需检查用户是否配置了 “mortgage calculator” lenders
  async function checkHasLenders() {
    if (
      SHOW_WIDGETS_FEATURES &&
      form.getFieldValue(["LandingPageSetUp", "showMortgage"])
    ) {
      const res = await request("/data/getcustomerlenders");
      if (!res?.Lenders?.some?.((v) => !v.Hidden && v.Active)) {
        message.warning(
          "Please add lenders before using the mortgage calculator and then publish your website.",
        );
        return Promise.reject();
      }
    }
  }

  useDebounceEffect(
    () => {
      if (sync) return;
      let env = "dev";
      if (!(process.env.NODE_ENV === "development")) {
        env = BLUERATE_ENV[window.location.host.split(".").at(-1)] || "www";
      }
      const data = form.getFieldsValue(["LastName", "FirstName", "NMLS"]);
      const firstName = (
        selectedBluerateOption?.firstName ||
        data?.FirstName ||
        ""
      ).replace(/ /g, "-");
      const lastName = (
        selectedBluerateOption?.lastName ||
        data?.LastName ||
        ""
      ).replace(/ /g, "-");
      const nmls = selectedBluerateOption?.nmls || data?.NMLS;
      const timestamp = Date.now();
      const previewCustomerID = selectedBluerateCustomerID || CUSTOMER_ID;
      setUrl({
        personal: form.getFieldValue("LandingUrl"),
        bluerate: `https://${env}.bluerate.ai/loan-officer/${firstName}-${lastName}-nmls-${nmls}?preview=true&customerID=${encodeURIComponent(previewCustomerID)}&date=${timestamp}`,
      });
    },
    [sync, selectedBluerateCustomerID, selectedBluerateOption],
    { wait: 500 },
  );

  const bluerateDescription = showChooseNewOrganization
    ? "Choose a new organization →"
    : !canEditBluerate && (selectedBluerateOrgName || selectedBluerateLabel)
      ? `Edit it in ${selectedBluerateOrgName || selectedBluerateLabel} →`
      : undefined;
  const chooseNewOrgTooltip = showChooseNewOrganization
    ? chooseNewOrganizationReason(
        profileCustomerID,
        companyOptions,
        selectedBluerateOrgName,
      )
    : undefined;
  const bluerateUrl =
    IS_ACTIVATED && canEditBluerate && !showChooseNewOrganization
      ? url?.bluerate?.split?.("?preview")?.at?.(0)
      : "";
  const personalUrl = IS_ACTIVATED ? `${origin}${url.personal}` : "";
  const stretchWebsiteInfo = Boolean(
    bluerateDescription || bluerateUrl || personalUrl,
  );

  return (
    <Layout
      loading={load}
      isUnInit={IS_UN_INIT}
      blueratePreviewLoading={blueratePreviewLoading}
      blueratePreviewReady={blueratePreviewReady}
      blueratePreviewDescription={blueratePreviewDescription}
      blueRateURL={showChooseNewOrganization ? "" : url.bluerate}
    >
      <h3>
        <span>Bluerate & Personal site</span>
        <Status status={status} />
        <Tooltip open={loading || sync} placement="right" title="Saving...">
          <Spin size="small" spinning={loading || sync} />
        </Tooltip>
        <Publish sync={sync} status={status} onClick={onPublishStatusChange} />
      </h3>

      <div className="edit-website-form">
        {IS_WAIT && (
          <p className="edit-website-msg">
            Verification is in progress and may take up to 24 hours. You will be
            automatically activated upon completion.
          </p>
        )}

        <WebsiteInfo
          loading={sync}
          title="Bluerate"
          className="bluerate"
          icon={ICON_BLUERATE}
          fullWidth={stretchWebsiteInfo}
          description={bluerateDescription}
          descriptionTooltip={chooseNewOrgTooltip}
          onDescriptionClick={
            showChooseNewOrganization
              ? () => companyInfoRef.current?.openPicker()
              : !canEditBluerate && selectedBluerateCustomerID
                ? goToBluerateOrganization
                : undefined
          }
          url={bluerateUrl}
        />

        <WebsiteInfo
          loading={sync}
          icon={ICON_WEBSITE}
          title="Personal site"
          fullWidth={stretchWebsiteInfo}
          url={personalUrl}
        >
          <Form.Item name="LandingUrl" noStyle>
            <EditPersonalSiteUrl />
          </Form.Item>
        </WebsiteInfo>

        <p className="edit-website-config">Configuration</p>

        <Collapse
          defaultValue
          className="website-customize-page website-company-organization"
          title="Company & organization"
        >
          <PersonalSiteCompanyInfo
            ref={companyInfoRef}
            company={data.company}
            options={companyOptions}
            optionsLoading={companyOptionsLoading}
            selectedCustomerId={selectedBluerateCustomerID}
            onRefreshOptions={refreshCompanyOptions}
          />
        </Collapse>

        {canEditBluerate && !showChooseNewOrganization && (
          <Collapse
            defaultValue
            className="website-customize-page"
            title="Bluerate page branding"
          >
            <EditPageBranding companyLogoURL={companyLogoURL(data.company?.Logo)} />
          </Collapse>
        )}

        <Collapse title="Profile information" status="COMPLETED">
          {PERSONAL_ITEMS.map((v) => (
            <v.component key={v.title} title={v.title} auditTips={AUDIT_TIPS} />
          ))}
        </Collapse>

        <Collapse
          defaultValue
          className="website-customize-page"
          title="Customize your page content"
        >
          {SHOW_WIDGETS_FEATURES && (
            <>
              <p>Widgets</p>

              <Form.Item
                initialValue
                valuePropName="checked"
                name={["LandingPageSetUp", "showMortgage"]}
                label={[
                  <img src={ICON_CALCULATOR} />,
                  "Mortgage rate calculator",
                ]}
              >
                <PageContentSwitch event={STATSIG.PERSONAL_SITE.MORTGAGE_CALCULATOR_SWITCH}>
                  <EditMortgageRateCalculator showEditBtn={SHOW_MORTGAGE} />
                </PageContentSwitch>
              </Form.Item>

              <Form.Item
                initialValue
                valuePropName="checked"
                name={["LandingPageSetUp", "showAffordability"]}
                label={[
                  <img src={ICON_CALCULATOR} />,
                  "Affordability calculator",
                ]}
              >
                <PageContentSwitch event={STATSIG.PERSONAL_SITE.AFFORDABILITY_CALCULATOR_SWITCH} />
              </Form.Item>
            </>
          )}

          <p
            className={SHOW_WIDGETS_FEATURES ? "website-customize-title " : ""}
          >
            Content
          </p>

          <Form.Item
            initialValue
            valuePropName="checked"
            name={["LandingPageSetUp", "showFAQ"]}
            label={[<img src={ICON_MESSAGE} />, "Frequently asked questions"]}
          >
            <PageContentSwitch event={STATSIG.PERSONAL_SITE.FAQ_SWITCH}>
              <Form.Item noStyle name={["LandingPageSetUp", "faqInfo"]}>
                <EditFAQs />
              </Form.Item>
            </PageContentSwitch>
          </Form.Item>

          <Form.Item
            initialValue
            valuePropName="checked"
            name={["LandingPageSetUp", "showTestimonial"]}
            label={[<img src={ICON_USER} />, "Customer testimonials"]}
          >
            <PageContentSwitch event={STATSIG.PERSONAL_SITE.TESTIMONIAL_SWITCH}>
              <Form.Item noStyle name={["LandingPageSetUp", "ReviewsList"]}>
                <EditTestimonial />
              </Form.Item>
            </PageContentSwitch>
          </Form.Item>
        </Collapse>

        <Collapse
          defaultValue
          className="website-customize-page"
          title="Home equity application links"
        >
          <p>
            Turn on the toggle to display a HELOC &amp; HELOAN section at the
            bottom of your rates section, then set where each button links to.
            Leave a field blank to hide that button. URLs must begin with
            “http://” or “https://”.
          </p>

          <Form.Item
            initialValue={false}
            valuePropName="checked"
            name={["LandingPageSetUp", "showHomeEquity"]}
            label="Display HELOC & HELOAN section"
          >
            <PageContentSwitch event={STATSIG.PERSONAL_SITE.HOME_EQUITY_SWITCH} />
          </Form.Item>

          <Form.Item
            name={["LandingPageSetUp", "helocLink"]}
            label="Apply for HELOC button URL (optional)"
            rules={[{ type: "url", message: "Please enter a valid URL" }]}
            tooltip={{
              title:
                'This field sets the URL for the "Apply for HELOC" button in your rates section.\n\nLeave it blank to hide the button. Make sure the URL begins with "http://" or "https://".',
              overlayStyle: { whiteSpace: "pre-line" },
            }}
          >
            <Input allowClear placeholder="Please enter the url (https://)" />
          </Form.Item>

          <Form.Item
            name={["LandingPageSetUp", "heloanLink"]}
            label="Apply for HELOAN button URL (optional)"
            rules={[{ type: "url", message: "Please enter a valid URL" }]}
            tooltip={{
              title:
                'This field sets the URL for the "Apply for HELOAN" button in your rates section.\n\nLeave it blank to hide the button. Make sure the URL begins with "http://" or "https://".',
              overlayStyle: { whiteSpace: "pre-line" },
            }}
          >
            <Input allowClear placeholder="Please enter the url (https://)" />
          </Form.Item>
        </Collapse>

        {IS_UN_INIT && (
          <div className="edit-website-complete">
            <Tooltip
              title={
                sync
                  ? "Data synchronization in progress, nearing completion"
                  : ""
              }
            >
              <Button
                type="primary"
                loading={load || sync}
                disabled={sync || load}
                onClick={onCompleteSetupPublish}
              >
                Complete setup & publish
              </Button>
            </Tooltip>
            <p>
              *By clicking publish, your profile will be listed on the Bluerate
              marketplace and your personal microsite will be activated.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EditPreview;
